import React, { useEffect, useMemo, useState } from "react";
import { useWallet } from './SignedWalletProvider'; // Adjust the import path

/**
 * ApiKeysManager
 * Props:
 *  - authToken: string (JWT bearer token)
 *  - baseUrl?: string (default "http://localhost:8000")
 *
 * Notes:
 *  - GET   {baseUrl}/api/keys              -> list keys
 *  - POST  {baseUrl}/api/key               -> create key  { name, in_ }
 *  - DELETE{baseUrl}/api/key/:key          -> invalidate key (adjust if your API differs)
 */
export default function ApiKeysManager({ config }) {
  const { jwtToken, network, account } = useWallet();
  const baseUrl = config.apiBaseUrl;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // new key form
  const [newName, setNewName] = useState("");
  const [newIn, setNewIn] = useState("header");

  const headers = useMemo(
    () => ({
      "Accept": "application/json",
      "Authorization": `Bearer ${jwtToken}`,
    }),
    [jwtToken]
  );

  async function fetchKeys() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/keys`, { headers });
      if (!res.ok) throw new Error(`Failed to load keys (${res.status})`);
      const data = await res.json();
      setKeys(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || "Failed to load keys");
    } finally {
      setLoading(false);
    }
  }

  async function createKey(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/key`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: newName.trim(), in_: newIn }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Create failed (${res.status}) ${txt || ""}`);
      }
      setNewName("");
      setNewIn("header");
      await fetchKeys();
    } catch (e) {
      setError(e.message || "Failed to create key");
    } finally {
      setCreating(false);
    }
  }

  async function invalidateKey(keyValue) {
    // If your API uses a different route or method, adjust here, e.g.:
    // POST `${baseUrl}/api/key/${encodeURIComponent(keyValue)}/invalidate`
    setError(null);
    try {
      const res = await fetch(`${baseUrl}/api/key/${encodeURIComponent(keyValue)}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Invalidate failed (${res.status}) ${txt || ""}`);
      }
      await fetchKeys();
    } catch (e) {
      setError(e.message || "Failed to invalidate key");
    }
  }

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, baseUrl]);

  // ---- helpers ----
  function formatRelative(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;

    const diffMs = d.getTime() - Date.now(); // future -> positive, past -> negative
    const abs = Math.abs(diffMs);

    const units = [
      ["year", 365 * 24 * 60 * 60 * 1000],
      ["month", 30 * 24 * 60 * 60 * 1000],
      ["day", 24 * 60 * 60 * 1000],
      ["hour", 60 * 60 * 1000],
      ["minute", 60 * 1000],
      ["second", 1000],
    ];
    for (const [label, ms] of units) {
      if (abs >= ms) {
        const val = Math.round(abs / ms);
        const s = val === 1 ? label : `${label}s`;
        return diffMs > 0 ? `in ${val} ${s}` : `${val} ${s} ago`;
      }
    }
    return "just now";
  }

  function copy(text) {
    navigator.clipboard?.writeText(text);
  }

  return (
    <div className="dark:bg-gray-900 dark:text-gray-100 min-h-[60vh] p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">API Keys</h1>
          <button
            onClick={fetchKeys}
            className="px-3 py-2 rounded-lg border border-gray-700 hover:bg-gray-800 transition"
            disabled={loading}
            title="Refresh"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </header>

        {/* Create Key */}
        <form
          onSubmit={createKey}
          className="bg-gray-800/50 border border-gray-700 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <div className="flex flex-col">
            <label className="text-sm text-gray-300 mb-1">Key Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g., KoolioKey2"
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              className="w-full md:w-auto bg-cyan-600 hover:bg-cyan-500 disabled:opacity-60 text-white px-4 py-2 rounded-lg transition"
              disabled={creating || !newName.trim()}
            >
              {creating ? "Creating…" : "Create Key"}
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-red-900/40 border border-red-700 text-red-200 px-4 py-2 rounded-lg">
            {error}
          </div>
        )}

        {/* Responsive API Keys List */}
        <div className="mt-6">
          <div className="flex flex-col md:flex-row gap-4 overflow-x-auto">
            {keys.length === 0 && !loading && (
              <div className="text-gray-400 px-4 py-8 text-center w-full border border-gray-700 rounded-lg">
                No keys yet. Create one above.
              </div>
            )}
            {keys.map((k) => {
              const createdRel = formatRelative(k.created_at);
              const expiresRel = k.expires_at ? formatRelative(k.expires_at) : "No expiration";
              return (
                <div
                  key={`${k.key}-${k.name}`}
                  className="flex flex-col md:flex-row md:items-center bg-gray-900 border border-gray-700 rounded-lg p-4 min-w-[280px] md:min-w-[320px] flex-shrink-0 w-full"
                >
                  <div className="font-semibold mb-2 md:mr-2 md:mb-0">{k.name}</div>
                  <div className="flex items-center gap-2 mb-2 md:mb-0 md:mr-2 w-full">
                    <code className="bg-gray-800 border border-gray-700 rounded px-2 py-1 break-all text-xs flex-1 md:mr-2 min-w-[150px]">
                      {k.key}
                    </code>
                    <button
                      type="button"
                      className="text-xs px-2 py-1 rounded border border-gray-700 hover:bg-gray-800 flex-shrink-0 md:mr-2"
                      onClick={() => copy(k.key)}
                      title="Copy key"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="text-gray-300 text-xs mb-1 md:mb-0 min-w-[100px]" title={k.created_at}>
                    Created: {createdRel}
                  </div>
                  <div className="text-gray-300 text-xs mb-2 md:mb-0 min-w-[100px]" title={k.expires_at || ""}>
                    Expiration: {expiresRel}
                  </div>
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-lg border border-red-700 text-red-200 hover:bg-red-900/30 text-xs mt-auto w-full max-w-[150px] md:ml-auto md:mt-0"
                    onClick={() => invalidateKey(k.key)}
                    title="Invalidate this key"
                  >
                    Invalidate
                  </button>
                </div>
              );
            })}
            {loading && (
              <div className="text-gray-400 px-4 py-6 text-center w-full border border-gray-700 rounded-lg">
                Loading…
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500">
          Tip: If your invalidate endpoint differs, update the <code>invalidateKey</code> function accordingly.
        </p>
      </div>
    </div>
  );
}
